import { useMediaPicker } from "@/app-bridge/hooks/useMediaPicker";
import { AD_TITLE_MAX } from "@/lib/plural";
import { createFileRoute, Link, useNavigate, useBlocker } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Crown, Loader2, MapPin, RefreshCw, Save, Video, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { startMuted } from "@/lib/video-mute";
import { adVideoSrc } from "@/lib/video-src";
import { createVideoUpload } from "@/lib/ads.functions";
import {
  getMyAdForEdit,
  updateAd,
  rewriteAdText,
  getMyRewriteQuota,
  getMyContactDefaults,
} from "@/lib/ads.functions";
import { checkAdText, checkAdQuality } from "@/lib/moderation.functions";
import { QUALITY_OK, type QualityCheck } from "@/lib/quality";
import type { AdTextCheck } from "@/lib/moderation";
import { getMyPlanStatus } from "@/lib/plans.functions";
import { getMyProfile } from "@/lib/profile.functions";
import { PLANS, planAtLeast } from "@/lib/plans";
import {
  AUCTION_DAYS,
  AUCTION_LOCK_MESSAGE,
  formatKr,
  timeLeftLabel,
  type AuctionLock,
} from "@/lib/auctions";

import { CATEGORIES } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { HelpLink } from "@/components/help/HelpLink";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ModerationNotice } from "@/components/ModerationNotice";
import { AiProgressModal, type AiProgressState } from "@/components/AiProgressModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AdImageEditor, type EditableImage } from "@/components/AdImageEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LocalityCombobox, LocalityHelp } from "@/components/LocalityCombobox";
import { LegalModal, type LegalDoc } from "@/components/legal/LegalModal";
import {
  StepLabel,
  FieldWrap,
  ContactCard,
  TermsAccept,
  MissingList,
} from "@/components/ad-form/AdFormPanel";
import { SavedPlacesPicker } from "@/components/SavedPlacesPicker";
import { SafeDealFields } from "@/components/SafeDealFields";
import { adFieldError, isValidLocalityForCountry } from "@/lib/ad-rules";

import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/redigera/$slug")({
  head: () => ({
    meta: [
      { title: "Redigera annons — Sellavo" },
      {
        name: "description",
        content: "Ändra titel, beskrivning, pris, ort och kontaktuppgifter på din annons.",
      },
      { property: "og:title", content: "Redigera annons — Sellavo" },
      {
        property: "og:description",
        content: "Ändra titel, beskrivning, pris, ort och kontaktuppgifter på din annons.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: EditAd,
});

const FIELD_MESSAGES: Record<string, string> = {
  title: "Skriv en rubrik.",
  description: "Skriv en beskrivning.",
  price: "Sätt ett pris.",
  category: "Välj en kategori.",
  location: "Välj en ort i listan.",
  contactName: "Visningsnamn saknas – fyll i din profil först.",
  contactEmail: "Ange en giltig e-postadress.",
};

/** Gör serverns tekniska valideringsfel till vanlig svenska. */
import { rememberReturn } from "@/lib/return-to";
import { BackLink } from "@/components/BackLink";
function friendlyError(err: unknown): string {
  const raw = (err instanceof Error ? err.message : String(err ?? "")).trim();
  if (!raw) return "Något gick fel. Försök igen.";
  if (raw.startsWith("[") || raw.startsWith("{")) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      const issues = Array.isArray(parsed)
        ? parsed
        : ((parsed as { issues?: unknown[] })?.issues ?? []);
      const messages = (issues as { path?: unknown[] }[])
        .map((issue) => FIELD_MESSAGES[String(issue?.path?.[0] ?? "")])
        .filter((m): m is string => !!m);
      if (messages.length) return Array.from(new Set(messages)).join(" ");
    } catch {
      // faller igenom till standardtexten nedan
    }
    return "Något i formuläret saknas eller är fel ifyllt. Gå igenom fälten och försök igen.";
  }
  return raw;
}

function EditAd() {
  const { slug } = Route.useParams();
  const returPath = `/redigera/${slug}`;

  const navigate = useNavigate();
  const fetchAd = useServerFn(getMyAdForEdit);
  const save = useServerFn(updateAd);

  const { data: ad, isLoading } = useQuery({
    queryKey: ["ad", slug],
    queryFn: () => fetchAd({ data: { slug } }),
  });

  const [form, setForm] = useState({
    title: "",
    description: "",
    price: "",
    category: "ovrigt",
    location: "",
    address: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
  });
  const [initial, setInitial] = useState<string | null>(null);
  // Auktionsvillkor (bara redigerbara innan första budet).
  const [reservePrice, setReservePrice] = useState("");
  const [auctionDays, setAuctionDays] = useState<"keep" | "3" | "7" | "14">("keep");

  const [saving, setSaving] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moderation, setModeration] = useState<{
    check: AdTextCheck;
    title: string;
    description: string;
  } | null>(null);
  const [quality, setQuality] = useState<QualityCheck | null>(null);
  const [images, setImages] = useState<EditableImage[]>([]);
  const [peopleConsent, setPeopleConsent] = useState(false);
  // Visas bara om granskningen faktiskt hittat en person på någon av de nya bilderna.
  const [peopleInImages, setPeopleInImages] = useState(false);
  const [video, setVideo] = useState<{ path: string; name: string; poster?: string | null } | null>(
    null,
  );
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoStatus, setVideoStatus] = useState<string | null>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const { pickVideo, sheet: mediaSheet } = useMediaPicker();
  const videoUploadFn = useServerFn(createVideoUpload);

  const planStatusFn = useServerFn(getMyPlanStatus);
  const { data: planStatus } = useQuery({ queryKey: ["my-plan"], queryFn: () => planStatusFn({}) });
  const maxImages = PLANS[planStatus?.plan ?? "free"].limits.maxImages;
  const profileFn = useServerFn(getMyProfile);
  const { data: profile } = useQuery({ queryKey: ["my-profile"], queryFn: () => profileFn({}) });
  const contactFn = useServerFn(getMyContactDefaults);
  const { data: contactDefaults } = useQuery({
    queryKey: ["my-contact-defaults"],
    queryFn: () => contactFn({}),
  });
  const rewriteFn = useServerFn(rewriteAdText);
  const quotaFn = useServerFn(getMyRewriteQuota);
  const { data: rewriteQuota, refetch: refetchQuota } = useQuery({
    queryKey: ["my-rewrite-quota"],
    queryFn: () => quotaFn({}),
  });
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [aiProgress, setAiProgress] = useState<AiProgressState | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [doc, setDoc] = useState<LegalDoc>(null);

  // Kontaktuppgifterna hör till kontot – annonsen visar dem bara.
  const contactName = profile?.displayName || contactDefaults?.contactName || form.contactName;
  const contactEmail = contactDefaults?.contactEmail || form.contactEmail;
  const contactPhone = contactDefaults?.contactPhone ?? form.contactPhone;

  const fieldError = useCallback(
    (
      id: "title" | "description" | "price" | "category" | "location" | "contactEmail",
    ): string | undefined => {
      if (!attempted) return undefined;
      // Samma regelverk som servern kör – ingen egen kopia här.
      return adFieldError(id, {
        title: form.title,
        description: form.description,
        price: form.price,
        category: form.category,
        location: form.location,
        country: ad?.country,
        contactEmail,
      });
    },

    [
      attempted,
      form.title,
      form.description,
      form.price,
      form.category,
      form.location,
      ad?.country,
      contactEmail,
    ],
  );

  const loadedAdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ad) return;
    // Ladda bara in annonsen en gång per annons – annars skrivs det man
    // just skrivit över när frågan hämtas om (t.ex. när fliken får fokus).
    if (loadedAdRef.current === slug) return;
    loadedAdRef.current = slug;
    const next = {
      title: ad.title,
      description: ad.description,
      price: String(ad.price),
      category: ad.category ?? "ovrigt",
      location: ad.location ?? "",
      address: ad.address ?? "",
      contactName: ad.contact_name ?? "",
      contactEmail: ad.contact_email ?? "",
      contactPhone: ad.contact_phone ?? "",
    };
    setForm(next);
    setReservePrice(ad.reserve_price ? String(ad.reserve_price) : "");
    setAuctionDays("keep");

    const vid = ad.video_url
      ? { path: ad.video_url, name: "Video på annonsen", poster: null }
      : null;
    setVideo(vid);
    if (vid) {
      void (async () => {
        const { extractVideoPoster } = await import("@/lib/video-poster");
        const poster = await extractVideoPoster(adVideoSrc(vid.path));
        if (poster) setVideo((v) => (v && v.path === vid.path ? { ...v, poster } : v));
      })();
    }
    const paths = (ad.image_urls?.length ? ad.image_urls : [ad.image_url]).filter(
      Boolean,
    ) as string[];
    const imgs: EditableImage[] = paths.map((path) => ({ kind: "existing" as const, path }));
    setImages(imgs);
    setInitial(JSON.stringify({ form: next, images: imgs, video: vid?.path ?? null }));
  }, [ad, slug]);

  const currentSnapshot = JSON.stringify({ form, images, video: video?.path ?? null });
  const dirty = initial !== null && currentSnapshot !== initial;
  // Osparade ändringar är det enda stället där vi stoppar dig på väg ut.
  const justSavedRef = useRef(false);
  const leaveBlocker = useBlocker({
    shouldBlockFn: () => dirty && !justSavedRef.current,
    enableBeforeUnload: () => dirty && !justSavedRef.current,
    withResolver: true,
  });

  // Auktion: villkoren är låsta så fort det finns bud.
  const auctionLock = (ad as { auctionLock?: AuctionLock } | undefined)?.auctionLock;
  const liveAuction = !!auctionLock?.isLive;
  const auctionLocked = !!auctionLock?.locked;

  const plan = planStatus?.plan ?? "free";
  const canVideo = PLANS[plan].limits.video;

  const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (videoInputRef.current) videoInputRef.current.value = "";
    if (!file) return;
    await uploadVideoFile(file);
  };

  const uploadVideoFile = async (file: File) => {
    setError(null);
    setVideoUploading(true);
    setVideoStatus("Förbereder videon…");
    try {
      const { prepareVideoForWeb } = await import("@/lib/video-compress");
      const prepared = await prepareVideoForWeb(file, (p) =>
        setVideoStatus(`Optimerar videon… ${p}%`),
      );

      setVideoStatus("Granskar ljudet i videon…");
      const { extractAudioBase64 } = await import("@/lib/video-audio");
      const audioBase64 = await extractAudioBase64(prepared.blob);
      const { checkAdAudio } = await import("@/lib/audio-moderation.functions");
      const audio = await checkAdAudio({
        data: audioBase64
          ? { audioBase64, mimeType: "audio/wav" }
          : { mimeType: "audio/wav", silent: true },
      });
      if (!audio.ok) {
        const { issuesToMessage } = await import("@/lib/moderation");
        throw new Error(
          `Ljudet i videon innehåller kontaktuppgifter. ${issuesToMessage(audio.issues)}`,
        );
      }
      if (!audio.clearanceId) throw new Error("Kunde inte granska ljudet. Försök igen.");

      setVideoStatus("Laddar upp videon…");

      const { path, token } = await videoUploadFn({
        data: {
          mimeType: prepared.mimeType,
          sizeBytes: prepared.blob.size,
          audioClearanceId: audio.clearanceId,
        },
      });

      const { error: upErr } = await supabase.storage
        .from("ad-images")
        .uploadToSignedUrl(path, token, prepared.blob, { contentType: prepared.mimeType });
      if (upErr) throw new Error(upErr.message);
      const { extractVideoPoster } = await import("@/lib/video-poster");
      let poster = await extractVideoPoster(
        new File([prepared.blob], `video.${prepared.ext}`, { type: prepared.mimeType }),
      );
      // Gick filen inte att läsa lokalt provar vi den uppladdade filmen.
      if (!poster) poster = await extractVideoPoster(adVideoSrc(path));
      setVideo({ path, name: prepared.label, poster });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunde inte ladda upp videon.");
    } finally {
      setVideoUploading(false);
      setVideoStatus(null);
    }
  };

  /** Kollar obligatoriska fält, markerar dem i gult och scrollar till det första. */
  const validateForm = () => {
    setAttempted(true);
    const order: ("title" | "description" | "price" | "category" | "location" | "contactEmail")[] =
      ["title", "description", "price", "category", "location", "contactEmail"];
    const firstBad = order.find((id) => fieldError(id));
    if (!firstBad) return true;
    setError(null);
    const el = document.getElementById(firstBad);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      window.setTimeout(() => el.focus({ preventScroll: true }), 350);
    }
    return false;
  };

  /** Läser annonsens bilder på nytt och föreslå ny text och pris. */
  const runRewrite = async () => {
    setRewriteOpen(false);
    setError(null);
    setRewriting(true);
    setAiProgress({
      steps: [
        "Granskar annonsens bilder",
        "Skriver rubrik och beskrivning",
        "Analyserar pris och kategori",
      ],
      done: false,
    });
    try {
      const result = await rewriteFn({ data: { slug } });
      setAiProgress({
        steps: [
          `Granskade ${result.imageCount} bild${result.imageCount === 1 ? "" : "er"}`,
          "Skrev rubrik och beskrivning",
          `Prisförslag ${result.price.toLocaleString("sv-SE")} kr`,
        ],
        notes: [result.imageNotes.join(" · "), "", result.category ?? ""],
        done: true,
      });
      setForm((prev) => ({
        ...prev,
        title: result.title,
        description: result.description,
        price: String(result.price),
        category: result.category ?? prev.category,
      }));
      void refetchQuota();
      window.setTimeout(() => setAiProgress(null), 1400);
    } catch (err) {
      setAiProgress(null);
      setError(friendlyError(err));
    } finally {
      setRewriting(false);
    }
  };

  const runSave = async (override?: { title: string; description: string }) => {
    if (!validateForm()) return;
    const title = override?.title ?? form.title;
    const description = override?.description ?? form.description;
    setError(null);
    if (!isValidLocalityForCountry(form.location, ad?.country)) {
      setError("Välj en ort i listan. Hittar du inte din ort, välj den närmaste.");
      return;
    }
    if (!images.length) {
      setError("Annonsen måste ha minst en bild.");
      return;
    }
    setSaving(true);

    try {
      if (!override) {
        const check = await checkAdText({ data: { title, description } });
        if (!check.ok) {
          setModeration({ check, title, description });
          setSaving(false);
          return;
        }
      }
      setModeration(null);

      // Kvalitetsgranskning – stoppar teckensallad och texter som blandar flera olika prylar.
      if (!override) {
        let q: QualityCheck = QUALITY_OK;
        try {
          q = await checkAdQuality({
            data: {
              title,
              description,
              images: images
                .filter((img): img is Extract<EditableImage, { kind: "new" }> => img.kind === "new")
                .slice(0, 3)
                .map((img) => ({ imageBase64: img.imageBase64, mimeType: img.mimeType })),
            },
          });
        } catch {
          q = QUALITY_OK;
        }
        if (q.verdict === "block" || q.imageWarnings.length) setQuality(q);
        if (q.verdict === "block") {
          setSaving(false);
          return;
        }
      } else {
        setQuality(null);
      }

      const result = await save({
        data: {
          slug,
          title,
          description,
          price: Number(form.price),
          category: form.category,
          location: form.location,
          address: form.address,
          contactName: contactName,
          contactEmail: contactEmail,
          contactPhone: contactPhone,
          images: images.map((img) =>
            img.kind === "existing"
              ? { path: img.path }
              : { imageBase64: img.imageBase64, mimeType: img.mimeType },
          ),
          peopleConsent,
          videoPath: video?.path ?? "",
          // Redigering fortsätter på annonsens ursprungsspråk även om användaren
          // tillfälligt läser sajten på ett annat språk.
          contentLanguage:
            ad?.content_language === "en" || ad?.content_language === "es"
              ? ad.content_language
              : "sv",
          ...(liveAuction && !auctionLocked
            ? {
                reservePrice: reservePrice ? Number(reservePrice) : null,
                ...(auctionDays !== "keep"
                  ? { auctionDays: Number(auctionDays) as 3 | 7 | 14 }
                  : {}),
              }
            : {}),
        },
      });
      if (result.needsPeopleConsent) {
        setPeopleInImages(true);
        setError(
          "Det ser ut att synas en person på någon av de nya bilderna. Intyga att du har samtycke, eller byt bild.",
        );
        return;
      }
      // Allt är sparat – släpp blockeringen innan vi navigerar vidare.
      justSavedRef.current = true;
      setInitial(currentSnapshot);
      navigate({ to: "/a/$slug", params: { slug } });
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setAttempted(true);
    if (!accepted) {
      document.getElementById("f-villkor")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    void runSave();
  };

  /** Samma "det här saknas"-lista som när man publicerar första gången. */
  const missing: { id: string; label: string }[] = [];
  if (!form.title.trim()) missing.push({ id: "title", label: "Rubriken är tom" });
  if (!form.description.trim()) missing.push({ id: "description", label: "Beskrivningen är tom" });
  if (!form.price || Number(form.price) <= 0)
    missing.push({ id: "price", label: "Sätt ett pris på annonsen" });
  if (!form.category) missing.push({ id: "category", label: "Välj en kategori" });
  if (!isValidLocalityForCountry(form.location, ad?.country))
    missing.push({ id: "location", label: "Välj en ort i listan eller använd en sparad adress" });
  if (!contactName)
    missing.push({ id: "f-profil", label: "Visningsnamn saknas – fyll i din profil" });
  if (!accepted) missing.push({ id: "f-villkor", label: "Godkänn användarvillkoren" });

  if (isLoading) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-16 text-muted-foreground">Laddar annons…</main>
    );
  }

  return (
    <main className="relative overflow-hidden bg-ocean-900 px-4 pb-16 pt-8 text-white sm:px-6">
      <div className="pointer-events-none absolute inset-0 opacity-20" aria-hidden="true">
        <div className="absolute -left-20 -top-20 h-72 w-72 rounded-full bg-ocean-500 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-ocean-300 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-xl">
        <BackLink className="text-ocean-100 hover:text-white" fallbackTo={`/a/${slug}`} />
        <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
          Redigera annonsen
        </h1>
        <p className="mt-2 text-ocean-100">
          Samma flöde som när du skapade den – justera texten, priset och platsen.
        </p>

        <AiProgressModal state={aiProgress} />
        <LegalModal doc={doc} onClose={() => setDoc(null)} />

        <AlertDialog
          open={leaveBlocker.status === "blocked"}
          onOpenChange={(open) => {
            if (!open) leaveBlocker.reset?.();
          }}
        >
          <AlertDialogContent className="sm:max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>Du har ändringar som inte är sparade</AlertDialogTitle>
              <AlertDialogDescription>
                Lämnar du nu försvinner ändringarna du gjort i annonsen. Spara först om du vill
                behålla dem.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => leaveBlocker.reset?.()}>
                Fortsätt redigera
              </AlertDialogCancel>
              <AlertDialogAction onClick={() => leaveBlocker.proceed?.()}>
                Lämna utan att spara
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog open={rewriteOpen} onOpenChange={setRewriteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Analysera om annonsen?</AlertDialogTitle>
              <AlertDialogDescription>
                Vi går igenom bilderna på nytt och ersätter rubrik, beskrivning och prisförslag med
                en ny bedömning. Du kan redigera allt efteråt och behöver spara för att ändringen
                ska gälla.
                {rewriteQuota && rewriteQuota.limit !== null
                  ? ` Det här använder 1 av dina ${rewriteQuota.remaining} kvarvarande omskrivningar.`
                  : ""}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Avbryt</AlertDialogCancel>
              <AlertDialogAction onClick={() => void runRewrite()}>Skriv om</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <form onSubmit={submit} className="mt-6 space-y-6">
          {/* Bilder och video */}
          <div className="space-y-4 rounded-md border border-white/10 bg-white/5 p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-ocean-100/70">
              Bilder{canVideo ? " och video" : ""}
            </p>
            <div className="[&_label]:text-ocean-100">
              <AdImageEditor
                images={images}
                onChange={setImages}
                maxImages={maxImages}
                onError={setError}
              />
            </div>
            {peopleInImages ? (
              <label className="flex items-start gap-3 rounded-md border border-amber-300/60 bg-amber-200/10 p-3 text-xs leading-relaxed text-ocean-50">
                <Checkbox
                  checked={peopleConsent}
                  onCheckedChange={(v) => setPeopleConsent(v === true)}
                  className="mt-0.5 border-ocean-300/60 data-[state=checked]:bg-ocean-300 data-[state=checked]:text-ocean-900"
                />
                <span>
                  <span className="font-semibold">Det syns en person på någon av bilderna.</span>{" "}
                  Jag intygar att alla identifierbara personer har lämnat samtycke till publicering
                  (för barn: samtycke från vårdnadshavare).
                </span>
              </label>
            ) : null}

            <div className="border-t border-white/10 pt-4">
              {canVideo ? (
                video ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex min-w-0 items-center gap-2 text-sm text-ocean-100">
                        <Video className="h-4 w-4 shrink-0 text-ocean-300" />
                        <span className="truncate">{video.name}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setVideo(null)}
                        className="rounded-sm p-1 text-ocean-100 transition-colors hover:text-white"
                        aria-label="Ta bort videon"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <video
                      ref={startMuted}
                      src={adVideoSrc(video.path)}
                      poster={video.poster ?? undefined}
                      controls
                      preload="metadata"
                      playsInline
                      className="aspect-video w-full rounded-md border border-white/10 bg-black object-contain"
                    />
                    <p className="text-[11px] text-ocean-100/70">
                      Första bildrutan visas som omslag – tryck på play för att se hela videon.
                      Ljudet finns kvar men startar avstängt.
                    </p>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      pickVideo({
                        onFile: (file) => void uploadVideoFile(file),
                        openWebPicker: () => videoInputRef.current?.click(),
                      })
                    }
                    disabled={videoUploading}
                    className="flex w-full items-center justify-center gap-2 text-sm font-medium text-ocean-100 transition-colors hover:text-white disabled:opacity-60"
                  >
                    {videoUploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Video className="h-4 w-4" />
                    )}
                    {videoUploading
                      ? (videoStatus ?? "Laddar upp video…")
                      : "Lägg till video (Pro)"}
                  </button>
                )
              ) : (
                <Link
                  to="/priser"
                  search={{ retur: returPath }}
                  onClick={() => rememberReturn(returPath)}
                  className="flex items-center justify-center gap-2 text-sm text-ocean-100 transition-colors hover:text-white"
                >
                  <Crown className="h-4 w-4 text-ocean-300" />
                  Video på annonsen ingår i Pro
                </Link>
              )}
              {mediaSheet}
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={handleVideoChange}
              />
            </div>
          </div>

          {/* Steg 1 — annonsen */}
          <div className="space-y-4 rounded-md border border-white/10 bg-white/5 p-5">
            <div className="flex items-start justify-between gap-3">
              <StepLabel step={1} title="Granska och justera" />
              <button
                type="button"
                onClick={() => setRewriteOpen(true)}
                disabled={rewriting || rewriteQuota?.remaining === 0}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-ocean-300/30 bg-ocean-700/30 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-ocean-700/50 disabled:opacity-60"
              >
                {rewriting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Analysera om</span>
              </button>
            </div>
            <p className="-mt-2 text-xs text-ocean-100/80">
              Vi läser annonsens bilder igen och föreslår ny rubrik, beskrivning och pris.
              {rewriteQuota
                ? rewriteQuota.limit === null
                  ? " Obegränsat med ditt Pro-konto."
                  : ` ${rewriteQuota.remaining} av ${rewriteQuota.limit} kvar den här månaden.`
                : ""}
              {rewriteQuota?.remaining === 0 ? (
                <>
                  {" "}
                  <Link
                    to="/priser"
                    search={{ retur: returPath }}
                    onClick={() => rememberReturn(returPath)}
                    className="text-ocean-300 underline underline-offset-2"
                  >
                    Se Plus och Pro
                  </Link>
                </>
              ) : null}
            </p>

            <FieldWrap label="Rubrik" htmlFor="title" error={fieldError("title")}>
              <Input
                id="title"
                value={form.title}
                placeholder="T.ex. Soffa i mörkblå sammet, 3-sits"
                maxLength={AD_TITLE_MAX}
                onChange={(e) => setForm({ ...form, title: e.target.value.slice(0, AD_TITLE_MAX) })}
                className={cn(
                  "bg-ocean-900/40 text-white placeholder:text-ocean-100/60",
                  fieldError("title")
                    ? "border-amber-300/70 ring-1 ring-amber-300/40"
                    : "border-white/20",
                )}
              />
              <p className="mt-1 text-xs text-ocean-100/70">
                {form.title.length}/{AD_TITLE_MAX} tecken
              </p>
            </FieldWrap>
            <FieldWrap label="Beskrivning" htmlFor="description" error={fieldError("description")}>
              <Textarea
                id="description"
                rows={6}
                placeholder="Skick, mått, ålder och varför du säljer – korta rader räcker."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className={cn(
                  "bg-ocean-900/40 text-white placeholder:text-ocean-100/60",
                  fieldError("description")
                    ? "border-amber-300/70 ring-1 ring-amber-300/40"
                    : "border-white/20",
                )}
              />
            </FieldWrap>
            <ModerationNotice
              title={form.title}
              description={form.description}
              result={
                moderation &&
                moderation.title === form.title &&
                moderation.description === form.description
                  ? moderation.check
                  : null
              }
              submitLabel="Ta bort och spara"
              onFix={(clean) => {
                setModeration(null);
                setForm({ ...form, title: clean.title, description: clean.description });
              }}
              onFixAndSubmit={(clean) => {
                setModeration(null);
                setForm({ ...form, title: clean.title, description: clean.description });
                void runSave(clean);
              }}
            />

            {quality && (quality.verdict === "block" || quality.imageWarnings.length > 0) && (
              <div className="rounded-lg border border-amber-300/30 bg-amber-200/10 p-4 text-sm leading-relaxed text-ocean-50">
                {quality.verdict === "block" && (
                  <>
                    <p className="mb-1.5 font-semibold text-white">Texten behöver justeras</p>
                    <ul className="space-y-1">
                      {quality.reasons.map((reason) => (
                        <li key={reason} className="flex gap-2">
                          <span className="text-amber-300">•</span>
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>
                    {quality.bundleKind === "mixed" && quality.items.length > 1 && (
                      <div className="mt-3 rounded-md border border-ocean-300/30 bg-ocean-700/30 p-3">
                        <p className="font-semibold text-white">
                          Vi hittar {quality.items.length} olika saker i annonsen
                        </p>
                        <p className="mt-1 text-ocean-100">
                          {quality.mixedReason ||
                            "En annons ska handla om en sak. Lägg ut de andra sakerna som egna annonser."}
                        </p>
                        <ul className="mt-2 space-y-2">
                          {quality.items.map((item) => (
                            <li
                              key={`${item.label}-${item.evidence}`}
                              className="rounded-md border border-white/10 bg-ocean-900/40 px-3 py-2"
                            >
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-white">{item.label}</span>
                                <span
                                  className={
                                    item.isPrimary
                                      ? "rounded-full bg-ocean-300/20 px-2 py-0.5 text-xs font-semibold text-ocean-200"
                                      : "rounded-full bg-amber-300/15 px-2 py-0.5 text-xs font-semibold text-amber-200"
                                  }
                                >
                                  {item.isPrimary ? "Behåll i annonsen" : "Egen annons"}
                                </span>
                              </div>
                              {item.evidence && (
                                <p className="mt-1 text-xs italic text-ocean-100/80">
                                  ”{item.evidence}”
                                </p>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {quality.rewrite && (
                      <button
                        type="button"
                        onClick={() => {
                          const clean = quality.rewrite!;
                          setQuality(null);
                          setForm({ ...form, title: clean.title, description: clean.description });
                          void runSave(clean);
                        }}
                        className="mt-3 rounded-md bg-ocean-300 px-3 py-1.5 text-xs font-semibold text-ocean-900"
                      >
                        {quality.items.find((i) => i.isPrimary)?.label
                          ? `Behåll bara ${quality.items.find((i) => i.isPrimary)!.label}`
                          : "Låt oss skriva om texten"}
                      </button>
                    )}
                  </>
                )}
                {quality.imageWarnings.length > 0 && (
                  <div className={quality.verdict === "block" ? "mt-3" : undefined}>
                    <p className="mb-1.5 font-semibold text-white">Om bilderna</p>
                    <ul className="space-y-1">
                      {quality.imageWarnings.map((warning) => (
                        <li key={warning} className="flex gap-2">
                          <span className="text-ocean-300">•</span>
                          <span>{warning}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {liveAuction && (
              <div
                className={cn(
                  "rounded-2xl border p-4 text-sm",
                  auctionLocked
                    ? "border-amber-300/40 bg-amber-300/10 text-amber-50"
                    : "border-white/15 bg-ocean-900/40 text-ocean-100",
                )}
              >
                <p className="font-semibold text-white">
                  Auktion pågår – slutar om {timeLeftLabel(auctionLock?.endsAt)}
                </p>
                <p className="mt-1">
                  {auctionLock?.bidCount ?? 0} bud
                  {auctionLock?.topBid ? ` · högsta ${formatKr(auctionLock.topBid)}` : ""}.{" "}
                  {auctionLocked
                    ? AUCTION_LOCK_MESSAGE
                    : "Du kan fortfarande ändra villkoren – de låses så fort första budet läggs."}
                </p>
              </div>
            )}

            <div className="grid items-start gap-4 sm:grid-cols-2">
              <FieldWrap
                label={liveAuction ? "Utgångspris (kr)" : "Pris (kr)"}
                htmlFor="price"
                error={fieldError("price")}
              >
                <Input
                  id="price"
                  type="number"
                  min={1}
                  placeholder="T.ex. 1 500"
                  value={form.price}
                  disabled={auctionLocked}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className={cn(
                    "bg-ocean-900/40 text-white placeholder:text-ocean-100/60",
                    auctionLocked && "cursor-not-allowed opacity-60",
                    fieldError("price")
                      ? "border-amber-300/70 ring-1 ring-amber-300/40"
                      : "border-white/20",
                  )}
                />
                {auctionLocked && (
                  <p className="mt-1.5 text-xs text-amber-100">Låst – auktionen har bud.</p>
                )}
              </FieldWrap>

              <FieldWrap label="Kategori" htmlFor="category" error={fieldError("category")}>
                <Select
                  key={form.category}
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger
                    id="category"
                    className={cn(
                      "h-10 bg-ocean-900/40 text-white",
                      fieldError("category")
                        ? "border-amber-300/70 ring-1 ring-amber-300/40"
                        : "border-white/20",
                    )}
                  >
                    <SelectValue placeholder="Välj kategori" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.slug} value={c.slug}>
                        <span className="inline-flex items-center gap-2">
                          <c.icon className="h-4 w-4 text-muted-foreground" />
                          {c.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldWrap>
            </div>

            {liveAuction && (
              <div className="grid items-start gap-4 sm:grid-cols-2">
                <FieldWrap label="Reservationspris (kr)" htmlFor="reserve">
                  <Input
                    id="reserve"
                    type="number"
                    min={1}
                    placeholder="Lägsta pris du säljer för"
                    value={
                      auctionLocked
                        ? auctionLock?.reservePrice
                          ? String(auctionLock.reservePrice)
                          : ""
                        : reservePrice
                    }
                    disabled={auctionLocked}
                    onChange={(e) => setReservePrice(e.target.value)}
                    className={cn(
                      "border-white/20 bg-ocean-900/40 text-white placeholder:text-ocean-100/60",
                      auctionLocked && "cursor-not-allowed opacity-60",
                    )}
                  />
                  {auctionLocked && (
                    <p className="mt-1.5 text-xs text-amber-100">Låst – auktionen har bud.</p>
                  )}
                </FieldWrap>

                <FieldWrap label="Sluttid" htmlFor="auction-days">
                  {auctionLocked ? (
                    <div className="flex h-10 items-center rounded-md border border-white/20 bg-ocean-900/40 px-3 text-sm text-ocean-100 opacity-70">
                      Slutar om {timeLeftLabel(auctionLock?.endsAt)} – låst
                    </div>
                  ) : (
                    <Select
                      value={auctionDays}
                      onValueChange={(v) => setAuctionDays(v as "keep" | "3" | "7" | "14")}
                    >
                      <SelectTrigger
                        id="auction-days"
                        className="h-10 border-white/20 bg-ocean-900/40 text-white"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="keep">
                          Behåll nuvarande ({timeLeftLabel(auctionLock?.endsAt)} kvar)
                        </SelectItem>
                        {AUCTION_DAYS.map((d) => (
                          <SelectItem key={d} value={String(d)}>
                            {d} dagar från nu
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </FieldWrap>
              </div>
            )}

            <div className="grid items-start gap-4 sm:grid-cols-2">
              <FieldWrap
                label="Ort"
                htmlFor="location"
                help={<LocalityHelp />}
                error={fieldError("location")}
              >
                <LocalityCombobox
                  id="location"
                  value={form.location}
                  onChange={(location) => setForm({ ...form, location })}
                  className={cn(
                    "bg-ocean-900/40 text-white",
                    fieldError("location")
                      ? "border-amber-300/70 ring-1 ring-amber-300/40"
                      : "border-white/20",
                  )}
                />
              </FieldWrap>
              <FieldWrap label="Gata eller område (valfritt)" htmlFor="address">
                <Input
                  id="address"
                  placeholder="T.ex. Majorna"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="border-white/20 bg-ocean-900/40 text-white placeholder:text-ocean-100/60"
                />
              </FieldWrap>
            </div>
            <SavedPlacesPicker
              location={form.location}
              address={form.address}
              highlight={attempted && !isValidLocalityForCountry(form.location, ad?.country)}
              onSelect={(p) => setForm({ ...form, location: p.location, address: p.address })}
            />
            <p className="flex items-start gap-2 text-xs text-ocean-100">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ocean-300" />
              Vi visar platsen på en karta i annonsen. Ange bara så exakt som du är bekväm med.
            </p>
          </div>

          {/* Steg 2 — kontakt och spara */}
          <div className="space-y-4 rounded-md border border-white/10 bg-white/5 p-5">
            <StepLabel step={2} title="Kontaktuppgifter och publicering" />
            <ContactCard
              name={contactName}
              email={contactEmail}
              phone={contactPhone}
              highlight={attempted && !contactName}
            />
            <TermsAccept
              checked={accepted}
              onChange={setAccepted}
              highlight={attempted && !accepted}
              onOpenDoc={setDoc}
              verb="spara"
            />

            {!planAtLeast(plan, "pro") && (
              <div className="rounded-md border border-white/10 bg-ocean-900/30 p-4">
                <p className="font-heading text-sm font-semibold text-white">
                  {plan === "free"
                    ? "Du har kontot Gratis – 3 bilder per annons."
                    : "Du har kontot Plus – 6 bilder per annons."}
                </p>
                <p className="mt-1 text-xs text-ocean-100">
                  {plan === "free"
                    ? "Plus ger 6 bilder och möjlighet att lyfta fram annonser. Pro lägger till video och obegränsat antal annonser."
                    : "Pro lägger till video på annonsen, fler lyft och obegränsat antal annonser."}
                </p>
                <Link
                  to="/priser"
                  search={{ retur: returPath }}
                  onClick={() => rememberReturn(returPath)}
                  className="mt-2 inline-block text-xs text-ocean-300 underline underline-offset-2"
                >
                  {plan === "free" ? "Se Plus och Pro" : "Uppgradera till Pro"}
                </Link>
              </div>
            )}

            <SafeDealFields slug={slug} />

            {attempted && missing.length > 0 && <MissingList items={missing} />}

            {error && (
              <p className="rounded-md border border-amber-300/60 bg-amber-200/10 px-3 py-2 text-sm text-ocean-50">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={saving || (!dirty && accepted)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-ocean-300 px-5 py-3 font-heading font-semibold text-ocean-900 transition-colors hover:bg-ocean-300/90 disabled:opacity-60"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sparar…
                </>
              ) : dirty ? (
                <>
                  <Save className="h-4 w-4" /> Spara ändringar
                </>
              ) : (
                "Inga ändringar gjorda"
              )}
            </button>
          </div>
        </form>
      </div>
      <div className="mt-8 flex justify-center">
        <HelpLink topic="annons" />
      </div>
    </main>
  );
}
